import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../client/query-client';
/** `GET /api/sos` — everything the overlay needs, in one read. */
export const SosStep = z.object({
    id: z.string().uuid(),
    key: z.string(),
    position: z.number().nonnegative(),
    label: z.string(),
    body: z.string(),
});
export const SosChip = z.object({
    id: z.string().uuid(),
    key: z.string(),
    position: z.number().nonnegative(),
    label: z.string(),
});
/**
 * Bounded server-side 1–10s (cycles 1–20), because it drives an animation a
 * parent follows while distressed — "a twenty-second inhale is not a
 * configuration choice, it is a mistake nobody would catch until someone tried
 * to breathe along with it."
 *
 * So the pacer reads these and never hard-codes 4-4-4: the seeded copy names
 * the numbers, and the timing and the words have to change together.
 */
export const SosBreathing = z.object({
    inhaleSeconds: z.number().int().min(1).max(10),
    holdSeconds: z.number().int().min(0).max(10),
    exhaleSeconds: z.number().int().min(1).max(10),
    cycles: z.number().int().min(1).max(20),
});
export const CrisisResource = z.object({
    key: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    phone: z.string().nullable(),
    url: z.string().nullable(),
});
export const SosOverlay = z.object({
    locale: z.string(),
    steps: z.array(SosStep),
    chips: z.array(SosChip),
    breathing: SosBreathing,
    resources: z.array(CrisisResource),
});
/**
 * The last-resort content.
 *
 * The backend refuses to let the last step be deleted "because a parent who
 * opens the overlay must be shown something". This is the client's half of that
 * guarantee: against a network that is down, the overlay still opens.
 *
 * FR because fr-CA is the source language and a parent in distress gets the
 * language the product was written in. The crisis line is Québec's.
 */
export const SOS_FALLBACK = {
    locale: 'fr',
    steps: [
        { id: 'fallback-1', key: 'breathe', position: 0, label: 'Respirer', body: 'Inspire… retiens… souffle. Reste ici un moment.' },
        { id: 'fallback-2', key: 'feel', position: 1, label: 'Nommer', body: 'Quelle émotion est là, en ce moment ?' },
        { id: 'fallback-3', key: 'express', position: 2, label: 'Exprimer', body: 'Mets des mots dessus, même maladroits.' },
        { id: 'fallback-4', key: 'reconnect', position: 3, label: 'Réparer', body: 'Réparer suffit. Reviens vers votre enfant quand vous êtes prêt.' },
    ],
    chips: [],
    breathing: { inhaleSeconds: 4, holdSeconds: 4, exhaleSeconds: 4, cycles: 4 },
    resources: [
        {
            key: 'ligneparents',
            name: 'LigneParents',
            description: 'Soutien professionnel gratuit, 24 h sur 24.',
            phone: '1 800 361-5085',
            url: 'https://www.ligneparents.com',
        },
    ],
};
/**
 * Prefetched at session start and cached hard. A parent opening this mid-crisis
 * must not wait on a cold request.
 *
 * `SOS_FALLBACK` is the placeholder rather than a loading state, so the overlay
 * has content from the first frame whatever the network is doing.
 */
export function useSos(http) {
    return useQuery({
        queryKey: queryKeys.sos,
        queryFn: () => http.get('/sos', { schema: SosOverlay }),
        staleTime: 60 * 60_000,
        gcTime: Infinity,
        placeholderData: SOS_FALLBACK,
        retry: 2,
    });
}
//# sourceMappingURL=sos.js.map